import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-location';

import { Category } from '@amzn/awsconcierge-magic-movie-machine-type-script-client';

import pageCatalog from 'pages/pageCatalog';
import { getMagicMovieAvatar, getMagicMovieUserName, getRecommendedMovies } from 'store/magicMovie';
import { useAppSelector } from 'store/utils/hooks';

import Movie from './Movie';
import Tabs from './Tabs';

import { recommendedMovieGridTabs } from '../constants';

import Avatar from '../components/avatar';
import { avatarColors, movieGridView } from '../constants';

interface RecommendedMoviesProps {
  onChangeView: (view: string) => void;
}

export const MagicMovieRecommendedMovies: React.FC<RecommendedMoviesProps> = (props) => {
  const { onChangeView } = props;
  const { t } = useTranslation(['magicMovie']);
  const navigate = useNavigate();
  const userName = useAppSelector(getMagicMovieUserName);
  const avatar = useAppSelector(getMagicMovieAvatar);
  const recommendedMovies = useAppSelector(getRecommendedMovies);
  const [selectedTab, setSelectedTab] = useState(Category.PERSONAL);
  const [selectedMovieSet, setSelectedMovieSet] = useState(
    recommendedMovies[Category.PERSONAL]?.length ? recommendedMovies[Category.PERSONAL] : []
  );

  const [heightValueOnResize, setHeightValueOnResize] = useState(300);
  const gridheaderRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  /** This logic pushed the movie grid down. We are handling on window resize because fonts get wrapped margin cannot be calculated appropriately in css */
  useEffect(() => {
    const domElement = document.getElementById('recommended-movie-outer-fixed-header');
    setHeightValueOnResize(domElement?.offsetHeight || 300);
    window.addEventListener('resize', function () {
      if (domElement?.offsetHeight) {
        setHeightValueOnResize(domElement?.offsetHeight);
      } else {
        setHeightValueOnResize(300);
      }
    });
  }, []);

  const getSelectedMoviesSet = (setName: string) => {
    return recommendedMovies[setName] || [];
  };

  const onTabChange = (selectedTab: string) => {
    const selectedMovieSet = getSelectedMoviesSet(selectedTab) || [];
    setSelectedTab(Category[selectedTab]);
    setSelectedMovieSet(selectedMovieSet);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div id="game" className="movies-root-padding">
      <div id="recommended-movies-grid-container">
        <div
          id="recommended-movie-outer-fixed-header"
          className="movie-grid-fixed-header"
          ref={gridheaderRef}
          style={{ top: -(heightValueOnResize - 150) }}
        >
          <div
            id="recommended-movie-inner-header"
            className="movie-grid-inner-header-container"
            data-test-id="recommended-movie-inner-header"
          >
            <div id="recommended-movie-header-left" data-test-id="recommended-movie-header-left">
              <div
                className={`rounded-avatar avatar-${avatar}`}
                style={{ backgroundColor: avatarColors[avatar].color }}
              >
                <Avatar id={avatar} peel={avatarColors[avatar].peel} />
              </div>
              <div>
                <h2 className="white" style={{ margin: '5px' }}>
                  {userName}
                </h2>
                <p className="blue header-text">{t('recommendedMovies.header')}</p>
              </div>
            </div>
            <button
              id="recommended-movie-finish-button"
              className="customButton primary movie-grid-header-button"
              onClick={() => {
                navigate({ to: pageCatalog['MagicMovieAdditionalResources'].route });
              }}
            >
              {t('recommendedMovies.buttonLabel')}
            </button>
          </div>
          <Tabs maxWidth={820} tabs={recommendedMovieGridTabs} selectedTab={selectedTab} onTabChange={onTabChange} />
        </div>
        <div id="recommended-movies-container">
          <div
            id="movie_content"
            className="movies-list-container"
            style={{
              marginTop: heightValueOnResize + 15 + 'px',
            }}
          >
            {selectedMovieSet?.map((movie) => {
              return (
                <Movie
                  id={movie.movieId}
                  key={movie.movieId}
                  clickEvents={false}
                  image={movie.imageUrl}
                  title={movie.name}
                  genre={movie.genres[0]}
                  recommendedMovie
                  rating={movie.rating}
                  score={movie.score}
                />
              );
            })}
          </div>
        </div>
        <div className="limitLine" style={{ margin: '0px auto', flex: 1, color: '#ffff' }}>
          {selectedMovieSet?.length > 0 && (
            <div className="insight">
              {selectedTab === Category.PERSONAL && [
                <h5 className="recommended_movies_dec_title" key="personal" style={{ margin: '5px 0px' }}>
                  {t('recommendedMovies.boxTitle1', { userName: userName })}
                </h5>,
                <p key="personal_dec" className="blue">
                  {t('recommendedMovies.boxDesc1')}
                </p>,
              ]}
              {selectedTab === Category.HIGH_RATED && [
                <h5 className="recommended_movies_dec_title" key="highly_rated" style={{ margin: '5px 0px' }}>
                  {t('recommendedMovies.boxTitle2', { userName: userName })}
                </h5>,
                <p key="highly_rated_dec" className="blue">
                  {t('recommendedMovies.boxDesc2')}
                </p>,
              ]}
              {selectedTab === Category.HIDDEN_GEMS && [
                <h5 className="recommended_movies_dec_title" key="hidden_gems" style={{ margin: '5px 0px' }}>
                  {t('recommendedMovies.boxTitle3', {
                    userName,
                  })}
                </h5>,
                <p key="hidden_gems_dec" className="blue">
                  {t('recommendedMovies.boxDesc3', { userName: userName })}
                </p>,
              ]}
              {selectedTab === Category.POPULAR && [
                <h5 className="recommended_movies_dec_title" key="Popular" style={{ margin: '5px 0px' }}>
                  {t('recommendedMovies.boxTitle4', { userName: userName })}
                </h5>,
                <p key="PopularDec" className="blue">
                  {t('recommendedMovies.boxDesc4', { userName: userName })}
                </p>,
              ]}
            </div>
          )}
          {selectedMovieSet?.length === 0 && (
            <div className="insight" style={{ borderColor: 'red' }}>
              <h5 className="recommended_movies_dec_title" key="personal" style={{ margin: '5px 0px' }}>
                {t(`recommendedMovies.boxErrTitle.${selectedTab}`)}
              </h5>
              <p key="err_dec" style={{ margin: '12px 0' }}>
                {t('recommendedMovies.boxErrDesc')}
              </p>
              <div
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <a
                  style={{
                    display: 'block',
                    fontSize: '1.5rem',
                  }}
                  onClick={() => {
                    onChangeView(movieGridView.popularMovies);
                  }}
                >
                  {t('recommendedMovies.boxErrRedirectLink')}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MagicMovieRecommendedMovies;
